import type { AccountBalance, Execution, Order, Position } from "@ekidenfi/ts-sdk";

import type { AccountStore } from "@stores/AccountStore";
import type { BalanceStore } from "@stores/BalanceStore";
import type { MarketStore } from "@stores/MarketStore";
import type { MyOrdersStore } from "@stores/MyOrdersStore";
import type { ToastStore } from "@stores/ToastStore";

import { Address } from "@utils/Address";
import {
  convertExecutionListToFillList,
  convertSDKOrderListToOrderList,
  convertSDKPositionListToPositionList,
} from "@utils/apiConverters";

import type { Market } from "@src/api";

import { getEkidenSdkClient } from "../sdk";

const REJECT_REASON_MESSAGES: Record<string, string> = {
  EcNoError: "No error",
  EcOthers: "An unexpected error occurred",
  EcInsufficientFunds: "Insufficient funds to place order",
  EcUnknownMessageType: "Unknown message type",
  EcMissingClOrdId: "Missing order ID",
  EcMissingOrigClOrdId: "Missing original order ID",
  EcClOrdIdOrigClOrdIdAreTheSame: "Order ID and original order ID cannot be the same",
  EcDuplicatedClOrdId: "Duplicate order ID",
  EcOrigClOrdIdDoesNotExist: "Original order does not exist",
  EcTooLateToCancel: "Too late to cancel order",
  EcUnknownOrderType: "Unknown order type",
  EcUnknownSide: "Unknown order side",
  EcUnknownTimeInForce: "Unknown time in force",
  EcWronglyRouted: "Order was wrongly routed",
  EcMarketOrderPriceIsNotZero: "Market order price must be zero",
  EcLimitOrderInvalidPrice: "Invalid limit order price",
  EcNoEnoughQtyToFill: "Not enough quantity to fill order",
  EcNoImmediateQtyToFill: "No immediate quantity available to fill",
  EcPerCancelRequest: "Cancelled by user request",
  EcMarketOrderCannotBePostOnly: "Market order cannot be post-only",
  EcPostOnlyWillTakeLiquidity: "Post-only order would take liquidity",
  EcCancelReplaceOrder: "Order cancelled for replacement",
  EcInvalidSymbolStatus: "Invalid market status",
  EcCancelForNoFullFill: "Cancelled due to partial fill not allowed",
  EcBySelfMatch: "Order would match with your own order",
  EcInCallAuctionStatus: "Market is in auction status",
  EcQtyCannotBeZero: "Order quantity cannot be zero",
  EcMarketOrderNoSupportTif: "Market order does not support this time in force",
  EcReachMaxTradeNum: "Maximum number of trades reached",
  EcInvalidPriceScale: "Invalid price precision",
  EcBitIndexInvalid: "Invalid bit index",
  EcStopBySelfMatch: "Stop order would self-match",
  EcInvalidSmpType: "Invalid self-match prevention type",
  EcCancelByMmp: "Cancelled by market maker protection",
  EcInvalidUserType: "Invalid user type",
  EcInvalidMirrorOid: "Invalid mirror order ID",
  EcInvalidMirrorUid: "Invalid mirror user ID",
  EcEcInvalidQty: "Invalid order quantity",
  EcInvalidAmount: "Invalid order amount",
  EcLoadOrderCancel: "Order cancelled during load",
  EcMarketQuoteNoSuppSell: "Market quote does not support sell",
  EcDisorderOrderId: "Disordered order ID",
  EcInvalidBaseValue: "Invalid base value",
  EcLoadOrderCanMatch: "Order can match during load",
  EcSecurityStatusFail: "Security status check failed",
  EcReachRiskPriceLimit: "Order exceeds risk price limit",
  EcOrderNotExist: "Order does not exist",
  EcCancelByOrderValueZero: "Cancelled due to zero order value",
  EcCancelByMatchValueZero: "Cancelled due to zero match value",
  EcReachMarketPriceLimit: "Order exceeds market price limit",
};

const getRejectReasonMessage = (reason: string | null | undefined): string => {
  if (!reason || reason === "EcNoError") return "Something went wrong. Please try again.";
  return REJECT_REASON_MESSAGES[reason] || "Something went wrong. Please try again.";
};

const MAX_KNOWN_ORDER_IDS = 1000;
const knownOrderIds = new Set<string>();

const addKnownOrderId = (orderId: string) => {
  // Clean up if exceeds limit - remove oldest half
  if (knownOrderIds.size >= MAX_KNOWN_ORDER_IDS) {
    const toRemove = Math.floor(MAX_KNOWN_ORDER_IDS / 2);
    let removed = 0;
    for (const id of knownOrderIds) {
      if (removed >= toRemove) break;
      knownOrderIds.delete(id);
      removed++;
    }
  }
  knownOrderIds.add(orderId);
};

interface PrivateStreamHandlers {
  order: (orders: Order[]) => void;
  position: (positions: Position[]) => void;
  execution: (fills: Execution[]) => void;
  account_balance: (update: AccountBalance) => void;
}

let isConnecting = false;
let isConnected = false;
let activeHandlers: Record<string, (data: unknown) => void> | null = null;

const setupConnection = async (
  ekidenSdkClient: ReturnType<typeof getEkidenSdkClient>,
  rootToken: string,
  handlers: PrivateStreamHandlers,
) => {
  if (isConnecting || isConnected) return;
  isConnecting = true;

  try {
    await ekidenSdkClient.setTokens({ rest: rootToken, ws: rootToken, connectPrivateWS: true });
    const castHandlers = handlers as unknown as Record<string, (data: unknown) => void>;
    ekidenSdkClient.subscribeHandlers(castHandlers);
    activeHandlers = castHandlers;
    isConnected = true;
  } finally {
    isConnecting = false;
  }
};

const reconnectWithNewToken = async (
  ekidenSdkClient: ReturnType<typeof getEkidenSdkClient>,
  accountStore: AccountStore,
  handlers: PrivateStreamHandlers,
) => {
  await accountStore.generateRootAccountToken(true);

  const newRootToken = accountStore.rootAccountToken;
  if (!newRootToken) {
    throw new Error("Failed to generate new root token");
  }

  isConnected = false;
  ekidenSdkClient.privateStream?.close();
  await setupConnection(ekidenSdkClient, newRootToken, handlers);
};

export const disconnect = async () => {
  const ekidenSdkClient = getEkidenSdkClient();
  if (activeHandlers) {
    ekidenSdkClient.unsubscribeHandlers(activeHandlers);
    activeHandlers = null;
  }
  isConnected = false;
  isConnecting = false;
  ekidenSdkClient.privateStream?.close();
};

export const resetConnectionState = () => {
  isConnected = false;
  isConnecting = false;
};

let cachedMarketsMap: Map<string, Market> | null = null;
let cachedMarketsRef: Market[] | null = null;

const getMarketsMap = (marketStore: MarketStore): Map<string, Market> => {
  if (cachedMarketsRef === marketStore.allMarkets && cachedMarketsMap) {
    return cachedMarketsMap;
  }
  cachedMarketsRef = marketStore.allMarkets;
  cachedMarketsMap = new Map(marketStore.allMarkets.map((m) => [m.addr, m]));
  return cachedMarketsMap;
};

export const connectAndSubscribe = async (
  rootToken: string,
  stores: {
    myOrdersStore: MyOrdersStore;
    accountStore: AccountStore;
    marketStore: MarketStore;
    balanceStore: BalanceStore;
    toastStore: ToastStore;
  },
) => {
  const ekidenSdkClient = getEkidenSdkClient();
  const { myOrdersStore, accountStore, marketStore, balanceStore, toastStore } = stores;

  if (!ekidenSdkClient.privateStream) {
    return;
  }

  const handlers: PrivateStreamHandlers = {
    order: (orders: Order[]) => {
      try {
        const activeTradingAccountAddr = accountStore.activeTradingAccount?.address;
        if (!activeTradingAccountAddr) {
          return;
        }

        const currentMarketsMap = getMarketsMap(marketStore);

        const filteredOrders = orders.filter((order) => {
          const orderAddr = order.sub_account_address;
          return orderAddr?.toLowerCase() === activeTradingAccountAddr.toLowerCase();
        });

        // Show notifications for new orders
        for (const order of filteredOrders) {
          const orderId = order.order_id;
          if (!orderId || knownOrderIds.has(orderId)) {
            continue;
          }

          addKnownOrderId(orderId);
          const status = order.order_status;

          if (status === "Rejected") {
            toastStore.show({
              title: "Order Rejected",
              content: getRejectReasonMessage(order.reject_reason),
              type: "error",
            });
          } else if (status === "New") {
            const side = order.side === "Buy" ? "Long" : "Short";
            const symbol = order.symbol?.toUpperCase() ?? "";
            const orderType = order.order_type ?? "Limit";
            toastStore.show({
              title: `${orderType} ${side} ${symbol} Opened`,
              content: "Order placed successfully",
              type: "success",
            });
          }
        }

        const convertedOrders = convertSDKOrderListToOrderList(filteredOrders, currentMarketsMap);
        for (const order of convertedOrders) {
          const actualLeverage = order.leverage;
          if (actualLeverage == null || Number.isNaN(actualLeverage)) continue;

          if (marketStore.confirmPendingLeverageChange(order.market_addr, actualLeverage)) {
            marketStore.setLeverages([
              {
                market_addr: order.market_addr,
                leverage: actualLeverage,
                sub_account_address: order.sub_account_address,
              },
            ]);
            toastStore.show({
              title: "Leverage Updated",
              content: `Leverage set to ${actualLeverage}x`,
              type: "success",
            });
          }
        }
        myOrdersStore.updateOrders(convertedOrders);
      } catch {}
    },
    position: (positions: Position[]) => {
      try {
        const activeTradingAccountAddr = accountStore.activeTradingAccount?.address;
        if (!activeTradingAccountAddr) {
          return;
        }

        const currentMarketsMap = getMarketsMap(marketStore);

        const filteredPositions = positions.filter((pos) => {
          const posAddr = pos.sub_account_address;
          return posAddr?.toLowerCase() === activeTradingAccountAddr.toLowerCase();
        });

        const convertedPositions = convertSDKPositionListToPositionList(filteredPositions, currentMarketsMap);

        for (const pos of convertedPositions) {
          const actualLeverage = pos.leverage;
          if (actualLeverage == null || Number.isNaN(actualLeverage)) continue;

          if (marketStore.confirmPendingLeverageChange(pos.market_addr, actualLeverage)) {
            marketStore.setLeverages([
              {
                market_addr: pos.market_addr,
                leverage: actualLeverage,
                sub_account_address: pos.sub_account_address,
              },
            ]);
            toastStore.show({
              title: "Leverage Updated",
              content: `Leverage set to ${actualLeverage}x`,
              type: "success",
            });
          }
        }

        myOrdersStore.updatePositions(convertedPositions);
      } catch {}
    },
    execution: (fills: Execution[]) => {
      try {
        const activeTradingAccountAddr = accountStore.activeTradingAccount?.address;
        if (!activeTradingAccountAddr) {
          return;
        }

        const currentMarketsMap = getMarketsMap(marketStore);

        const filteredFills = fills.filter((fill) => {
          const fillAddr = fill.sub_account_address;
          return fillAddr?.toLowerCase() === activeTradingAccountAddr.toLowerCase();
        });

        const convertedFills = convertExecutionListToFillList(
          filteredFills,
          activeTradingAccountAddr,
          currentMarketsMap,
        );
        myOrdersStore.addNewFills(convertedFills);
      } catch {}
    },
    account_balance: (update: AccountBalance | Array<AccountBalance>) => {
      try {
        if (Array.isArray(update)) {
          for (const balance of update) {
            const eventSubAddr = Address.from(balance.sub_account_address || "").toString();
            balanceStore.applyAccountBalanceUpdate({
              sub_account_address: eventSubAddr,
              equity: Number(balance.equity ?? 0),
              available_balance: Number(balance.available_balance ?? 0),
              total_margin_used: Number(balance.total_position_im ?? 0),
            });
          }
        } else {
          const eventSubAddr = Address.from(update.sub_account_address || "").toString();
          balanceStore.applyAccountBalanceUpdate({
            sub_account_address: eventSubAddr,
            equity: Number(update.equity ?? 0),
            available_balance: Number(update.available_balance ?? 0),
            total_margin_used: Number(update.total_position_im ?? 0),
          });
        }
      } catch {}
    },
  };

  try {
    await setupConnection(ekidenSdkClient, rootToken, handlers);
  } catch {
    try {
      await reconnectWithNewToken(ekidenSdkClient, accountStore, handlers);
    } catch {}
  }
};
